import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import {
  Panel,
  theme,
  Button,
  Text,
  Flex,
  TabView,
  Dropdown,
  Box,
  Spinner,
} from '@xometry/xometry_loft';
import { Link as ReactLink } from 'react-router-dom';
import {
  CreateBillingAccountQuery,
  GetTaxExemptionsQuery,
  GetBillingAccountUserQuery,
  SetBillingAccountAddressQuery,
} from '../../schema/generated/schema';
import BillingProfile from './BillingProfile';
import BillingProfileForm from './BillingProfileForm';
import { deepTrimAndRemoveDuplicateSpaces } from '../../utils/helpers';
import TaxExemptions from './TaxExemptions';
import Financials from './Financials';
import { useFeatureFlag, FEATURE_FLAGS } from '../../hooks';

const BillingProfiles = ({ userID, isStaff }) => {
  const isFinancialsEnabled = useFeatureFlag(
    FEATURE_FLAGS.CustomerPortalFinancials
  );
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [selectedBillingAccountIndex, setSelectedBillingAccountIndex] =
    useState(0);
  const [taxExemptionList, setTaxExemptionList] = useState([]);
  const [selectedTaxExemption, setSelectedTaxExemption] = useState([]);
  const [editBillingProfileIndex, setEditBillingProfileIndex] = useState(-1);
  const [showNew, setShowNew] = useState(false);
  const [submitErrors, setSubmitErrors] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  /** Controlled tab index so we do not override TabView.Header.Tab onClick (that breaks tab switching). */
  const [billingInfoTabIndex, setBillingInfoTabIndex] = useState(0);

  const setBillingTabIndex = (index) => {
    setBillingInfoTabIndex(index);
    if (isFinancialsEnabled && index === 2 && window.analytics) {
      window.analytics.track('Financials Tab Viewed', {
        object_id: 'financials-tab',
        object_name: 'Financials Tab',
        object_location: 'Billing Information',
      });
    }
  };

  const [createBillingAccount] = useMutation(CreateBillingAccountQuery);
  const [setBillingAccountAddress] = useMutation(SetBillingAccountAddressQuery);

  const { loading } = useQuery(GetBillingAccountUserQuery, {
    variables: {
      userId: userID,
    },
    onCompleted: (data) => {
      setBillingAccounts(data.getBillingAccountUser.billingAccounts.edges);
    },
  });

  useQuery(GetTaxExemptionsQuery, {
    variables: {
      userId: userID,
    },
    onCompleted: (data) => {
      setTaxExemptionList([...data.getTaxExemptions]);
    },
  });

  const getExemptionsByBillingAccount = (billingAccountId) =>
    taxExemptionList.filter(
      (exemption) => exemption.billingAccountId === billingAccountId
    );

  // Listen to a change in selected billing account, and then check to see if that account
  // has any associated tax exemptions to render.
  useEffect(() => {
    setSelectedTaxExemption(
      getExemptionsByBillingAccount(
        billingAccounts[selectedBillingAccountIndex]?.node?.id
      )
    );
  }, [selectedBillingAccountIndex, taxExemptionList]);

  const editBillingProfileActive = (index) => {
    setEditBillingProfileIndex(index);
  };

  const editBillingProfileDeactive = () => {
    setEditBillingProfileIndex(-1);
  };

  const setAddBillingProfileActive = () => {
    setShowNew(true);
    editBillingProfileDeactive();
  };

  const setAddBillingProfileDeactive = () => {
    setShowNew(false);
    editBillingProfileDeactive();
  };

  // Function used to add a new Billing Profile
  const postAddBillingProfile = (values) => {
    // set createdBy to customer's userID, even if a staff member is the one creating the Billing Profile
    values.createdBy = userID;
    values.isCustomer = true;
    const cleanedValues = deepTrimAndRemoveDuplicateSpaces(values);
    setSubmitLoading(true);
    setSubmitErrors('');
    return createBillingAccount({ variables: { input: cleanedValues } })
      .then(
        () => {
          window.location.reload();
        },
        (e) => {
          const errorMssgs = e.graphQLErrors.map(
            (errorObj) => errorObj.message
          );
          setSubmitErrors(
            errorMssgs.length > 0
              ? errorMssgs.join(', ')
              : 'An error occured submitting your Billing Profile. If the problem persists please contact us at support@xometry.com'
          );
        }
      )
      .finally(() => setSubmitLoading(false));
  };

  // Function used to edit a Billing Profile
  const postUpdateBillingProfile = (values) => {
    // Only allow updating address fields
    const addressValues = values.contact.address;
    const cleanedValues = deepTrimAndRemoveDuplicateSpaces(addressValues);
    setSubmitLoading(true);
    setSubmitErrors('');
    return setBillingAccountAddress({
      variables: {
        input: {
          billingAccountId: billingAccounts[editBillingProfileIndex].node.id,
          address: cleanedValues,
        },
      },
    })
      .then(
        () => {
          if (window.analytics) {
            window.analytics.track('Billing Profile Created', {
              staffCreated: isStaff ? 'Yes' : 'No',
            });
          }
          window.location.reload();
        },
        (e) => {
          const errorMssgs = e.graphQLErrors.map(
            (errorObj) => errorObj.message
          );
          setSubmitErrors(
            errorMssgs.length > 0
              ? errorMssgs.join(', ')
              : 'An error occured updating your Billing Profile. If the problem persists please contact us at support@xometry.com'
          );
        }
      )
      .finally(() => setSubmitLoading(false));
  };

  return (
    <Box>
      <Text
        id="billing-information"
        kind="subpage-title"
        display="block"
        marginBottom={theme.space[4]}
      >
        Billing Information
      </Text>
      {loading && <Spinner />}
      {!showNew &&
        selectedBillingAccountIndex !== editBillingProfileIndex &&
        !loading && (
          <Box maxWidth="792px" width="100%">
            <Panel kind="primary" padding="0">
              <TabView
                pb="1px"
                selectedTab={billingInfoTabIndex}
                setSelectedTabInParent={setBillingTabIndex}
              >
                <TabView.Header>
                  <TabView.Header.Tab
                    width="fit-content"
                    marginRight="4px"
                    padding="12px"
                  >
                    Payment Terms
                  </TabView.Header.Tab>
                  <TabView.Header.Tab
                    width="fit-content"
                    marginLeft="4px"
                    marginRight={isFinancialsEnabled ? '4px' : undefined}
                    padding="12px"
                  >
                    Tax Exemptions
                  </TabView.Header.Tab>
                  {isFinancialsEnabled && (
                    <TabView.Header.Tab
                      width="fit-content"
                      marginLeft="4px"
                      padding="12px"
                    >
                      Financials
                    </TabView.Header.Tab>
                  )}
                </TabView.Header>
                <TabView.Content>
                  <TabView.Content.Item>
                    {billingAccounts.length > 0 && (
                      <Box margin={'12px 20px 20px'}>
                        <Flex
                          flexDirection="row"
                          justifyContent="space-between"
                          marginBottom="12px"
                        >
                          {billingAccounts.length === 1 ? (
                            <Text className="pii-encrypt" kind="body-title">
                              {
                                billingAccounts[selectedBillingAccountIndex]
                                  .node.name
                              }
                            </Text>
                          ) : (
                            <Dropdown
                              alignment="left"
                              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            >
                              <Dropdown.Trigger>
                                <Button kind="text-blue">
                                  <Text
                                    kind="body-title"
                                    color={
                                      isDropdownOpen
                                        ? theme.colors.blues[100]
                                        : ''
                                    }
                                  >
                                    {
                                      billingAccounts[
                                        selectedBillingAccountIndex
                                      ].node.name
                                    }
                                  </Text>
                                  <i
                                    className={`fa fa-caret-${
                                      isDropdownOpen ? 'up' : 'down'
                                    }`}
                                    css={css`
                                      color: ${theme.colors.darkBlues[50]};
                                      margin-left: ${theme.space[2]};
                                    `}
                                  />
                                </Button>
                              </Dropdown.Trigger>
                              <Dropdown.Content maxWidth="300px">
                                {billingAccounts.map(({ node }, index) => {
                                  // We don't want to display the currently rendered billing acct in our list
                                  if (index === selectedBillingAccountIndex)
                                    return <div key={index} />;
                                  return (
                                    <Dropdown.Option
                                      key={index}
                                      onClick={() => {
                                        setSelectedBillingAccountIndex(index);
                                      }}
                                    >
                                      {node.name}
                                    </Dropdown.Option>
                                  );
                                })}
                              </Dropdown.Content>
                            </Dropdown>
                          )}
                          <Text
                            className="pii-encrypt"
                            kind="body-sm-title"
                            fontWeight={theme.fontWeights.semibold}
                          >
                            Account ID:{' '}
                            {
                              billingAccounts[selectedBillingAccountIndex].node
                                .id
                            }
                          </Text>
                        </Flex>
                        <BillingProfile
                          key={selectedBillingAccountIndex}
                          index={selectedBillingAccountIndex}
                          profile={
                            billingAccounts[selectedBillingAccountIndex].node
                          }
                          isStaff={isStaff}
                          userID={userID}
                          onEditClick={editBillingProfileActive}
                        />
                      </Box>
                    )}
                    {/* If no existing Billing Profiles then show creation form or New button */}
                    {billingAccounts.length === 0 && (
                      <Flex
                        alignItems="center"
                        flexDirection="column"
                        margin="20px"
                      >
                        <Flex
                          marginBottom="20px"
                          flexDirection={['column', 'row']}
                        >
                          <img
                            src="https://assets.xometry.com/images/BillingIllustrationNoTextStep1.svg"
                            alt="Create Billing Profile"
                            css={css`
                              max-height: 167px;
                            `}
                          />

                          <img
                            src="https://assets.xometry.com/images/BillingIllustrationNoTextStep2.svg"
                            alt="Apply For Terms"
                            css={css`
                              max-height: 167px;
                            `}
                          />

                          <img
                            src="https://assets.xometry.com/images/BillingIllustrationNoTextStep3.svg"
                            alt="Pay With Purchase Order"
                            css={css`
                              max-height: 167px;
                            `}
                          />
                        </Flex>
                        <Text
                          kind="body-sm"
                          display="block"
                          textAlign="center"
                          marginBottom="20px"
                        >
                          Add your billing contact information and complete the
                          Terms Setup Form to get access to payment terms and
                          pay by Purchase Order. Your organization&apos;s
                          EIN/Tax ID is required and financial documentation may
                          be necessary to get on terms.
                        </Text>
                        <Button
                          kind={theme.kind.primary}
                          padding={`${theme.space[1]} ${theme.space[2]}`}
                          onClick={() => {
                            if (window.analytics) {
                              window.analytics.track(
                                'Billing Profile CTA Clicked',
                                {
                                  staffCreated: isStaff ? 'Yes' : 'No',
                                }
                              );
                            }
                            setAddBillingProfileActive(true);
                          }}
                        >
                          Add Billing Contact
                        </Button>
                      </Flex>
                    )}
                  </TabView.Content.Item>
                  <TabView.Content.Item>
                    {selectedTaxExemption.length > 0 && (
                      <TaxExemptions
                        taxExemptions={selectedTaxExemption}
                        isStaff={isStaff}
                        userID={userID}
                        profile={
                          billingAccounts[selectedBillingAccountIndex].node
                        }
                      />
                    )}
                    {selectedTaxExemption.length === 0 && (
                      <Flex
                        alignItems="center"
                        flexDirection="column"
                        margin="20px"
                      >
                        <img
                          src="https://assets.xometry.com/images/Spot_Upload.svg"
                          alt="File Upload"
                          css={css`
                            max-height: 188px;
                          `}
                        />
                        <Text
                          kind="body-sm"
                          display="block"
                          textAlign="center"
                          margin="20px auto"
                        >
                          Add your billing contact information to upload a tax
                          exemption. If your organization has an eligible
                          exemption, upload it now and it will be applied at
                          checkout. Multiple exemptions can be added to the same
                          account.
                        </Text>
                        {billingAccounts.length === 0 && (
                          <Button
                            kind={theme.kind.primary}
                            padding={`${theme.space[1]} ${theme.space[2]}`}
                            onClick={() => {
                              if (window.analytics) {
                                window.analytics.track(
                                  'Billing Profile CTA Clicked',
                                  {
                                    staffCreated: isStaff ? 'Yes' : 'No',
                                  }
                                );
                              }
                              setAddBillingProfileActive(true);
                            }}
                          >
                            Add Billing Contact
                          </Button>
                        )}
                        {/* Display add tax exempt link if address is valid and in the US */}
                        {billingAccounts.length > 0 &&
                          billingAccounts[selectedBillingAccountIndex].node
                            .contact.address.country === 'United States' &&
                          /^[\d-]+$/.test(
                            billingAccounts[selectedBillingAccountIndex].node
                              .contact?.address?.zipcode
                          ) && (
                            <ReactLink
                              to={{
                                pathname: `/add-tax-exemption${
                                  isStaff ? `/${userID}` : ''
                                }`,
                              }}
                              state={{
                                billingAccount:
                                  billingAccounts[selectedBillingAccountIndex]
                                    .node,
                              }} // Pass billing account id to tax exemption component
                            >
                              <Button kind={theme.kind.primaryOutline}>
                                <i className="fa fa-circle-plus" /> Add
                                Exemption
                              </Button>
                            </ReactLink>
                          )}
                      </Flex>
                    )}
                  </TabView.Content.Item>
                  {isFinancialsEnabled && (
                    <TabView.Content.Item>
                      {billingAccounts.length > 0 && (
                        <Financials
                          billingAccountId={
                            billingAccounts[selectedBillingAccountIndex].node.id
                          }
                        />
                      )}
                      {billingAccounts.length === 0 && (
                        <Flex
                          alignItems="center"
                          flexDirection="column"
                          margin="20px"
                        >
                          <Text
                            kind="body-sm"
                            display="block"
                            textAlign="center"
                            margin="20px auto"
                          >
                            Add your billing contact information to view
                            invoices and payments.
                          </Text>
                          <Button
                            kind={theme.kind.primary}
                            padding={`${theme.space[1]} ${theme.space[2]}`}
                            onClick={() => {
                              if (window.analytics) {
                                window.analytics.track(
                                  'Billing Profile CTA Clicked',
                                  {
                                    staffCreated: isStaff ? 'Yes' : 'No',
                                  }
                                );
                              }
                              setAddBillingProfileActive(true);
                            }}
                          >
                            Add Billing Contact
                          </Button>
                        </Flex>
                      )}
                    </TabView.Content.Item>
                  )}
                </TabView.Content>
              </TabView>
            </Panel>
          </Box>
        )}
      {showNew && (
        <BillingProfileForm
          key="new-billing-profile-form"
          showMode="New"
          onSave={postAddBillingProfile}
          onCancelClick={setAddBillingProfileDeactive}
          submitErrors={submitErrors}
          loading={submitLoading}
        />
      )}
      {selectedBillingAccountIndex === editBillingProfileIndex && (
        <BillingProfileForm
          key="edit-billing-profile-form"
          showMode="Edit"
          billingProfile={billingAccounts[selectedBillingAccountIndex].node}
          onSave={postUpdateBillingProfile}
          onCancelClick={editBillingProfileDeactive}
          submitErrors={submitErrors}
          loading={submitLoading}
        />
      )}
    </Box>
  );
};

export default BillingProfiles;
